import { useEffect } from 'react';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Divider, Drawer, Link, useMediaQuery } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { Logo } from '../logo';
import { MainSidebarSection, SidebarItem } from './main-sidebar-section';
import { OrganizationSelector } from '../../componentsv2/navigation/OrganizationSelector';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import ConstructionIcon from '@mui/icons-material/Construction';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import DashboardIcon from '@mui/icons-material/Dashboard';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import HandymanIcon from '@mui/icons-material/Handyman';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import MoodIcon from '@mui/icons-material/Mood';
import PermMediaIcon from '@mui/icons-material/PermMedia';
import PlayCircleOutlinedIcon from '@mui/icons-material/PlayCircleOutlined';
import PushPinIcon from '@mui/icons-material/PushPin';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import SettingsIcon from '@mui/icons-material/Settings';
import { userHasFieldScope } from '@/services/acl/fieldPermissions';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import SupportIcon from '@mui/icons-material/Support';
import TuneIcon from '@mui/icons-material/Tune';
import UndoIcon from '@mui/icons-material/Undo';
import StoreIcon from '@mui/icons-material/Store';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import { Scrollbar } from '../misc/Scrollbar';
import { useOrganizationData } from '../../contexts/OrganizationContext';
import { SuggestedFixesChip } from '../../components/astur/channels/suggested-fixes-chip';
import { OrderIssuesSidebarChip } from '../../componentsv2/order-issues/OrderIssuesSidebarChip';
import { AISuggestedFixesChip } from '../../components/astur/channels/ai-suggested-fixes-chip';
import { UnhealthyChip } from '../../components/astur/channels/unhealthy-chip';
import CrisisAlertIcon from '@mui/icons-material/CrisisAlert';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import RuleFolderIcon from '@mui/icons-material/RuleFolder';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { UsersIcon } from '../../componentsv2/icons/UsersIcon';
import { User } from '../../types/user';
import { utils } from '../../utils';
import { useCommittedOrganization } from '@/session/OrganizationIdentityContext';
import { useOrganizationList } from '@/session/useOrganizationList';
import { useSessionIdentityGeneration } from '@/session/useSessionIdentityGeneration';
import { getSessionRealmRoles } from '@/session/token';
import { useSettlementsAccess } from '@/hooks/useSettlementsAccess';
import { superAdminSurfaceRoles } from '@/utils/superAdminSurfaces';
import { services } from '@/services';

export interface MainSidebarProps {
  open: boolean;
  onClose: () => void;
  user: User | null;
}

export type Section = {
  // Omitted for the headerless Dashboard group that sits above the first titled section.
  title?: string;
  require_superuser: boolean;
  items: SidebarItem[];
};

// Gates that cannot be expressed as a conjunction of SidebarItem's require_* fields, resolved by
// the component (which can call hooks) and handed to getSections as pre-computed booleans.
export type SidebarAccess = {
  // Channel-settlement money surfaces: an Acenda staff role in any organization, or the
  // `settlements` tag on this one. See src/utils/settlementsAccess.ts.
  settlements: boolean;
  users: boolean;
  customFields: boolean;
};

export const getSections = (t: (key: string) => string, access: SidebarAccess): Section[] => [
  {
    require_superuser: false,
    items: [
      {
        title: t('Dashboard'),
        path: '/',
        icon: <DashboardIcon />,
        require_scope: 'organization:read',
      },
    ],
  },
  {
    title: t('Orders'),
    require_superuser: false,
    items: [
      {
        title: t('Orders'),
        icon: <CreditCardIcon />,
        path: '/orders',
      },
      {
        title: t('Order Issues'),
        icon: <ReportProblemIcon />,
        path: '/order-issues',
        require_scope: 'order:read',
        extra: <OrderIssuesSidebarChip />,
      },
      {
        title: t('Returns'),
        icon: <UndoIcon />,
        path: '/returns',
      },
      {
        // Visible to Acenda super users / management in every organization, and to every member of
        // an organization tagged `settlements`. The gate is canAccessSettlements
        // (src/utils/settlementsAccess.ts) — the same predicate the page guard and the per-order
        // Settlement tab call, so a row's deep-link never lands on a hidden Settlement tab.
        title: t('Settlements'),
        path: '/ledger-reconciliation',
        icon: <AccountBalanceIcon />,
        require_access: access.settlements,
      },
    ],
  },
  {
    title: t('Catalog'),
    require_superuser: false,
    items: [
      {
        title: t('Items'),
        path: '/items',
        icon: <ShoppingBasketIcon />,
      },
      {
        title: t('Assets'),
        path: '/assets',
        require_scope: 'file:read',
        icon: <PermMediaIcon />,
      },
      {
        title: t('Attribute Overrides'),
        path: '/attribute-overrides',
        icon: <PushPinIcon />,
      },
      {
        title: t('Custom Fields'),
        path: '/custom-fields',
        icon: <TuneIcon />,
        require_access: access.customFields,
      },
    ],
  },
  {
    title: t('Channels'),
    require_superuser: false,
    items: [
      {
        title: t('Item Issues'),
        path: '/suggested-fixes',
        icon: <ConstructionIcon />,
        extra: <SuggestedFixesChip />,
      },
      {
        title: t('Item Issues (AI)'),
        path: '/suggested-fixes/unified',
        icon: <AutoAwesomeIcon />,
        extra: <AISuggestedFixesChip />,
      },
      {
        title: t('Item Status'),
        path: '/item-statuses',
        icon: <ListAltIcon />,
      },
      {
        title: t('Integrations'),
        path: '/integrations',
        icon: <StoreIcon />,
        extra: <UnhealthyChip />,
      },
      {
        title: t('Channel Expansion'),
        path: '/channel-expansion',
        icon: <CallSplitIcon />,
        require_role: superAdminSurfaceRoles('channel_expansion'),
        match_descendants: true,
      },
      {
        title: t('Warehouses'),
        icon: <WarehouseIcon />,
        path: '/warehouses',
      },
    ],
  },
  {
    title: t('Data Transfers'),
    require_superuser: false,
    items: [
      {
        // The import-export page opens on the Imports tab when no `tab` is in the URL.
        title: t('Imports'),
        path: '/import-export?tab=imports',
        icon: <FileUploadIcon />,
        query_is_default: true,
      },
      {
        title: t('Exports'),
        path: '/import-export?tab=exports',
        icon: <FileDownloadIcon />,
      },
      {
        title: t('Scheduled Exports'),
        path: '/import-export/exports/recurring',
        icon: <EventRepeatIcon />,
        match_descendants: true,
      },
    ],
  },
  {
    title: t('Reporting'),
    require_superuser: false,
    items: [
      {
        title: t('Reports'),
        path: '/reports',
        require_scope: 'report:read',
        icon: <AssessmentIcon />,
      },
    ],
  },
  {
    title: t('Administration'),
    require_superuser: false,
    items: [
      {
        title: t('Settings'),
        path: '/settings',
        icon: <SettingsIcon />,
        require_scope: 'organization:read',
      },
      {
        title: t('Users'),
        path: '/users',
        icon: <UsersIcon fontSize='small' />,
        require_access: access.users,
      },
    ],
  },
  {
    title: t('Help'),
    require_superuser: false,
    items: [
      {
        // Branch paths are never matched or linked; a '#' sentinel cannot collide with a route.
        title: t('Help & Resources'),
        path: '#help-and-resources',
        icon: <HelpOutlineIcon />,
        children: [
          {
            title: t('Help Desk'),
            path: utils.constants.ASTUR_DEFAULTS.helpDeskLink,
            isPathExternal: true,
            icon: <SupportIcon />,
          },
          {
            title: t('Video Guides'),
            path: utils.constants.ASTUR_DEFAULTS.vimeoVideoLink,
            isPathExternal: true,
            icon: <PlayCircleOutlinedIcon />,
          },
          {
            title: t('Share Feedback'),
            path: 'https://share.hsforms.com/1S2h8G9owQma29PBui6zxxg3eq1q',
            isPathExternal: true,
            icon: <MoodIcon />,
          },
        ],
      },
    ],
  },
  {
    title: t('Super Admin'),
    require_superuser: true,
    // Every entry takes its required roles from SUPER_ADMIN_SURFACES, the same map the route
    // guard reads, so a visible menu item always links to a page the user can actually open.
    items: [
      {
        title: t('Organizations Admin'),
        path: '/organizations_admin',
        icon: <StoreIcon />,
        require_role: superAdminSurfaceRoles('organizations_admin'),
      },
      {
        title: t('Integrations Admin'),
        path: '/integrations_admin',
        icon: <IntegrationInstructionsIcon />,
        require_role: superAdminSurfaceRoles('integrations_admin'),
      },
      {
        title: t('Dashboard Alerts Admin'),
        path: '/dashboard_alerts_admin',
        icon: <CrisisAlertIcon />,
        require_role: superAdminSurfaceRoles('dashboard_alerts_admin'),
      },
      {
        title: t('SPC Health'),
        path: '/service_platform_calls_health_admin',
        icon: <MonitorHeartIcon />,
        require_role: superAdminSurfaceRoles('service_platform_calls_health_admin'),
      },
      {
        // Hidden when the viewer holds neither child's role.
        title: t('ATTS Tooling'),
        path: '#atts-tooling',
        icon: <HandymanIcon />,
        children: [
          {
            title: t('Global Commonality Mappings'),
            path: '/atts_commonality_global_admin',
            icon: <RuleFolderIcon />,
            require_role: superAdminSurfaceRoles('atts_commonality_global_admin'),
          },
          {
            title: t('Category Plans'),
            path: '/category_plans_admin',
            icon: <AccountTreeIcon />,
            require_role: superAdminSurfaceRoles('category_plans_admin'),
            match_descendants: true,
          },
        ],
      },
    ],
  },
];

export const MainSidebar = (props: MainSidebarProps) => {
  const { onClose, open, user } = props;
  const router = useRouter();
  const { t } = useTranslation();
  const lgUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'), {
    noSsr: true,
  });

  const { hasFeature } = useOrganizationData();
  // Non-conjunctive audience (staff role OR organization tag), so it cannot be expressed with the
  // require_* fields and is computed here instead. `resolving` is ignored on purpose: hiding a nav
  // entry on a pre-commit render is self-correcting once the identity/membership lands.
  const { allowed: canAccessSettlementSurfaces } = useSettlementsAccess();
  const organization = useCommittedOrganization();
  const { organizations, error: organizationsError, retry, retrying } = useOrganizationList();
  // The role gates below are module reads, not props: subscribe so the Super Admin section
  // appears on the identity commit that publishes realm roles.
  useSessionIdentityGeneration();
  const canAccessUsers = services.user.availableOrganizationAdministrationTabs(user, getSessionRealmRoles()).length > 0;
  const canAccessCustomFields = ['product', 'order', 'ship_advice'].some((model) => userHasFieldScope(user, model, 'read'));
  const sections = getSections(t, { settlements: canAccessSettlementSurfaces, users: canAccessUsers, customFields: canAccessCustomFields });

  const handlePathChange = () => {
    if (!router.isReady) {
      return;
    }
    if (open) {
      onClose?.();
    }
  };

  useEffect(
    handlePathChange,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [router.isReady, router.asPath]
  );

  const content = (
    <>
      <Box
        data-testid='main-sidebar-content'
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: '0 0 0 100%',
          flexGrow: 1,
          overflow: 'hidden',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', flex: '0 0 auto', minHeight: '150px' }}>
          <Box sx={{ p: 3 }}>
            <Link
              component={NextLink}
              href={`/${organization}`}
              aria-label={t('Dashboard') as string}
              sx={{ display: 'flex', width: '209px', maxWidth: '62.5%', m: 'auto' }}
            >
              <Logo />
            </Link>
          </Box>

          <Box sx={{ px: 2, minHeight: '75px' }}>
            <OrganizationSelector organizations={organizations ?? []} />
            {organizationsError && (
              <Alert
                severity='warning'
                sx={{ mt: 1 }}
                action={
                  <Button color='inherit' disabled={retrying} onClick={retry} size='small'>
                    {t(retrying ? 'Retrying…' : 'Retry')}
                  </Button>
                }
              >
                {t('The organization list may be out of date.')}
              </Alert>
            )}
          </Box>
        </div>
        <Divider
          sx={{
            borderColor: '#2D3748',
            my: 3,
          }}
        />
        <Scrollbar style={{ display: 'flex', flexDirection: 'column', height: '75%' }}>
          <Box>
            {sections.map((section, index) =>
              section.require_superuser && !utils.authUtils.isAcendaSuperOrManagement() ? null : (
                <MainSidebarSection
                  key={index}
                  path={router.asPath}
                  sx={{
                    // A headerless group has no subheader to fill the space under the divider, so
                    // it drops its own top margin and padding to keep the sidebar rhythm even.
                    mt: section.title ? 2 : 0,
                    pt: section.title ? undefined : 0,
                    '& + &': {
                      mt: 2,
                    },
                  }}
                  title={section.title}
                  items={section.items}
                  user={user}
                  require_superuser={section.require_superuser ? true : false}
                  hasFeature={hasFeature}
                />
              )
            )}
          </Box>
        </Scrollbar>
      </Box>
    </>
  );

  if (lgUp) {
    return (
      <Drawer
        anchor='left'
        open
        PaperProps={{
          sx: {
            backgroundColor: 'neutral.900',
            borderRightColor: 'divider',
            borderRightStyle: 'solid',
            borderRightWidth: (theme) => (theme.palette.mode === 'dark' ? 1 : 0),
            color: '#FFFFFF',
            width: 280,
          },
        }}
        variant='permanent'
      >
        {content}
      </Drawer>
    );
  }

  return (
    <Drawer
      anchor='left'
      onClose={onClose}
      open={open}
      PaperProps={{
        sx: {
          backgroundColor: 'neutral.900',
          color: '#FFFFFF',
          width: 280,
        },
      }}
      sx={{ zIndex: (theme) => theme.zIndex.appBar + 100 }}
      variant='temporary'
    >
      {content}
    </Drawer>
  );
};
